import { zodResolver } from '@hookform/resolvers/zod';
import { ForwardRefRenderFunction, forwardRef, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  MemberStopQuayDetailsFragment,
  MemberStopStopPlaceDetailsFragment,
} from '../../../../../../../generated/graphql';
import { Operation, useLoader } from '../../../../../../../redux';
import { EnrichedParentStopPlace } from '../../../../../../../types';
import {
  KnownValueKey,
  findKeyValue,
  mapLngLatToPoint,
  notNullish,
  showSuccessToast,
} from '../../../../../../../utils';
import { mapToISODate, today } from '../../../../../../../utils/time';
import { FormActionButtons } from '../../../../../../common/Forms';
import { useDirtyFormBlockNavigation } from '../../../../../../common/Forms/NavigationBlocker';
import { InputField } from '../../../../../../common/Inputs';
import {
  Column,
  FormColumn,
  FormRow,
} from '../../../../../../common/LayoutComponents';
import { AlternativeNamesEdit } from '../../../../../Components/AlternativeNames';
import { SelectedStop } from '../../../../../Components/SelectMemberStops/common';
import { TerminalTypeDropdown } from '../../../../../Components/TerminalTypeDropdown';
import { TerminalType } from '../../../../../Types/TerminalType';
import { TerminalFormState, terminalFormSchema } from './schema';
import { useUpdateTerminalDetails } from './useUpdateTerminalDetails';

const testIds = {
  privateCode: 'TerminalDetailsEdit::privateCode',
  description: 'TerminalDetailsEdit::description',
  name: 'TerminalDetailsEdit::name',
  nameSwe: 'TerminalDetailsEdit::nameSwe',
  terminalType: 'TerminalDetailsEdit::terminalType',
  departurePlatforms: 'TerminalDetailsEdit::departurePlatforms',
  arrivalPlatforms: 'TerminalDetailsEdit::arrivalPlatforms',
  loadingPlatforms: 'TerminalDetailsEdit::loadingPlatforms',
  electricCharging: 'TerminalDetailsEdit::electricCharging',
};

function mapQuayToSelectedStop(
  terminal: EnrichedParentStopPlace,
  stopPlace: MemberStopStopPlaceDetailsFragment,
  quay: MemberStopQuayDetailsFragment,
): SelectedStop {
  const validityStart = mapToISODate(
    findKeyValue(quay, KnownValueKey.ValidityStart),
  );
  const validityEnd = mapToISODate(
    findKeyValue(quay, KnownValueKey.ValidityEnd),
  );
  return {
    stopPlaceId: stopPlace?.id ?? '',
    stopPlaceParentId: terminal.id ?? null,
    name: stopPlace?.name?.value ?? '',
    quayId: quay?.id ?? '',
    publicCode: quay?.publicCode ?? '',
    validityStart: validityStart ?? '',
    validityEnd: validityEnd ?? '',
    indefinite: !validityEnd,
  };
}

function extractSelectedStops(terminal: EnrichedParentStopPlace) {
  return (
    terminal.children
      ?.filter(notNullish)
      .flatMap(
        (child) =>
          child.quays
            ?.filter(notNullish)
            .map((quay) => mapQuayToSelectedStop(terminal, child, quay)) ?? [],
      ) ?? []
  );
}

export function mapTerminalDataToFormState(
  terminal: EnrichedParentStopPlace,
): TerminalFormState {
  const { latitude, longitude } = mapLngLatToPoint(
    terminal.geometry?.coordinates ?? [],
  );

  return {
    privateCode: terminal.privateCode?.value ?? '',
    description: {
      lang: terminal.description?.lang ?? null,
      value: terminal.description?.value ?? null,
    },
    name: terminal.name ?? '',
    nameSwe: terminal.nameSwe ?? '',
    nameEng: terminal.nameEng,
    nameLongFin: terminal.nameLongFin,
    nameLongSwe: terminal.nameLongSwe,
    nameLongEng: terminal.nameLongEng,
    abbreviationFin: terminal.abbreviationFin,
    abbreviationSwe: terminal.abbreviationSwe,
    abbreviationEng: terminal.abbreviationEng,
    validityStart: mapToISODate(terminal.validityStart) ?? today().toString(),
    validityEnd: mapToISODate(terminal.validityEnd),
    indefinite: !terminal.validityEnd,
    terminalType: terminal.terminalType
      ? (terminal.terminalType as TerminalType)
      : undefined,
    departurePlatforms: terminal.departurePlatforms,
    arrivalPlatforms: terminal.arrivalPlatforms,
    loadingPlatforms: terminal.loadingPlatforms,
    electricCharging: terminal.electricCharging,
    latitude,
    longitude,
    selectedStops: extractSelectedStops(terminal),
  };
}

type TerminalDetailsEditProps = {
  readonly terminal: EnrichedParentStopPlace;
  readonly className?: string;
  readonly onFinishEditing: () => void;
  readonly onCancel: () => void;
  readonly testIdPrefix: string;
};

const TerminalDetailsEditImpl: ForwardRefRenderFunction<
  HTMLFormElement,
  TerminalDetailsEditProps
> = ({ terminal, className, onFinishEditing, onCancel, testIdPrefix }, ref) => {
  const { t } = useTranslation();

  const { updateTerminalDetails, defaultErrorHandler } =
    useUpdateTerminalDetails();
  const { setIsLoading } = useLoader(Operation.ModifyTerminal);
  const onSubmit = async (state: TerminalFormState) => {
    setIsLoading(true);
    try {
      await updateTerminalDetails({ terminal, state });

      showSuccessToast(t(($) => $.terminalDetails.editSuccess));
      onFinishEditing();
    } catch (err) {
      defaultErrorHandler(err as Error, state);
    }
    setIsLoading(false);
  };

  const defaultValues = useMemo(
    () => mapTerminalDataToFormState(terminal),
    [terminal],
  );
  const methods = useForm<TerminalFormState>({
    defaultValues,
    resolver: zodResolver(terminalFormSchema),
  });
  useDirtyFormBlockNavigation(methods.formState, 'TerminalDetailsEdit');
  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <form className={className} onSubmit={handleSubmit(onSubmit)} ref={ref}>
        <FormColumn>
          <FormRow lgColumns={3} mdColumns={2}>
            <Column>
              <InputField<TerminalFormState>
                type="text"
                translationPrefix="terminalDetails.basicDetails"
                fieldPath="privateCode"
                testId={testIds.privateCode}
                disabled
              />
            </Column>
            <Column>
              <InputField<TerminalFormState>
                type="text"
                translationPrefix="terminalDetails.basicDetails"
                fieldPath="name"
                testId={testIds.name}
                required
              />
            </Column>
            <Column>
              <InputField<TerminalFormState>
                type="text"
                translationPrefix="terminalDetails.basicDetails"
                fieldPath="nameSwe"
                testId={testIds.nameSwe}
              />
            </Column>
          </FormRow>
          <FormRow mdColumns={1}>
            <Column>
              <InputField<TerminalFormState>
                type="text"
                translationPrefix="stopDetails"
                fieldPath="description.value"
                testId={testIds.description}
                customTitlePath="terminalDetails.basicDetails.description"
              />
            </Column>
          </FormRow>
          <AlternativeNamesEdit />
          <FormRow lgColumns={5} mdColumns={2}>
            <Column>
              <InputField<TerminalFormState>
                translationPrefix="terminalDetails.basicDetails"
                fieldPath="terminalType"
                testId={testIds.terminalType}

                inputElementRenderer={(props) => (
                  <TerminalTypeDropdown {...props} />
                )}
              />
            </Column>
            <Column>
              <InputField<TerminalFormState>
                type="text"
                translationPrefix="terminalDetails.basicDetails"
                fieldPath="departurePlatforms"
                testId={testIds.departurePlatforms}
              />
            </Column>
            <Column>
              <InputField<TerminalFormState>
                type="text"
                translationPrefix="terminalDetails.basicDetails"
                fieldPath="arrivalPlatforms"
                testId={testIds.arrivalPlatforms}
              />
            </Column>
            <Column>
              <InputField<TerminalFormState>
                type="text"
                translationPrefix="terminalDetails.basicDetails"
                fieldPath="loadingPlatforms"
                testId={testIds.loadingPlatforms}
              />
            </Column>
            <Column>
              <InputField<TerminalFormState>
                type="text"
                translationPrefix="terminalDetails.basicDetails"
                fieldPath="electricCharging"
                testId={testIds.electricCharging}
              />
            </Column>
          </FormRow>
        </FormColumn>
        <FormActionButtons
          onCancel={onCancel}
          testIdPrefix={testIdPrefix}
          isDisabled={
            !methods.formState.isDirty || methods.formState.isSubmitting
          }
          isSubmitting={methods.formState.isSubmitting}
          variant="infoContainer"
        />
      </form>
    </FormProvider>
  );
};

export const TerminalDetailsEdit = forwardRef(TerminalDetailsEditImpl);
